import { spUrl } from "utils";
import {
	buildSearchParams,
	put,
	patch,
	remove,
	request,
	save,
} from "utils/proxy";
import {
	API_AWARD,
	API_FORMS,
	API_PROPOSAL,
	API_SUBAWARD,
	COLUMN_HEADER_KEYS,
	FLOWABLE_API_FORM_DEFINITIONS,
	FLOWABLE_BASE_URL,
	FLOWABLE_DESIGN_URL,
} from "globals/constants";


export const form = {
	/**
	 * @param {{ id: string }} params
	 * @param {{}?} options
	 */
	get: (params, options) => request(spUrl(`${API_FORMS}/${params.id}`), options),

	saveTemplate: (formType, body, options) => save(spUrl(`${API_FORMS}/template/${formType}`), body, options),

	saveDraft: (id, body, options) => save(spUrl(`${API_FORMS}/draft/${id}`), body, options),

	getModel: (id, options) => request(`${FLOWABLE_API_FORM_DEFINITIONS}/${id}/model`, options),

	findDefinitions: (searchParams, options) =>
		request(`${FLOWABLE_API_FORM_DEFINITIONS}${buildSearchParams(searchParams)}`, options),

	getAllModels: (options) => request(`${FLOWABLE_DESIGN_URL}/cayuse-api/models/forms`, options),

	getFormDeployments: (options) => request(`${FLOWABLE_BASE_URL}/cayuse-api/form-deployments`, options),

	getDesignModel: (params, options) => request(`${FLOWABLE_DESIGN_URL}/cayuse-api/models/forms/${params.id}`, options),

	getSimplifiedModel: (id, options) =>
		request(`${FLOWABLE_DESIGN_URL}/cayuse-api/simplified-modeler/models/${id}`, options),

	updateSimplifiedModel: (id, body, options) =>
		save(`${FLOWABLE_DESIGN_URL}/cayuse-api/simplified-modeler/models/${id}`, body, options),

	publish: (modelId, body, options) =>
		save(`${FLOWABLE_DESIGN_URL}/app/app-definitions/${modelId}/publish`, body, options),

	getModelList: (formId, options) =>
		request(`${FLOWABLE_DESIGN_URL}/app/models?filter=apps&modelType=3&sort=modifiedDesc&wsId=${formId}`, options),

	/**
	 * @param {{id: string}} params
	 * @param {{}?} options
	 */
	getVariables: (params, options) =>
		request(spUrl(`${API_FORMS}/case-instance/${params.id}/variables?modificationNumber=${params.modificationNumber ?? "" }`), options),

	getLastPublishedElements: (key, options) =>
		request(`${FLOWABLE_API_FORM_DEFINITIONS}?key=${key}&latest=true`, options),

	updateRules: (id, body, options) => put(spUrl(`${API_FORMS}/${id}/rules`), body, options),

	/**
	 * @param {{type: string, id: string}} params
	 * @param {{}?} options
	 */
	getReviewedSection: (params, options) => {
		switch (params.type) {
			case COLUMN_HEADER_KEYS.AWARD:
				return request(spUrl(`${API_AWARD}/${params.id}/checkmarks`), options);
			case COLUMN_HEADER_KEYS.PROPOSAL:
				return request(spUrl(`${API_PROPOSAL}/${params.id}/checkmarks`), options);
			case COLUMN_HEADER_KEYS.SUBAWARD:
				return request(spUrl(`${API_SUBAWARD}/${params.subawardId}/checkmarks`), options);
			default:
				return Promise.reject(new Error(`Unexpected type: ${params.type}`));
		}
	},

	/**
	 * @param {{type: string, tab: string, id: string, subawardId?: string }} params
	 * @param {{version: string}} queryParams
	 * @param {{}?} options
	 */
	removeReviewedSection: (params, queryParams, options) => {
		let searchParams = buildSearchParams(queryParams);

		switch (params.type) {
			case COLUMN_HEADER_KEYS.AWARD:
				return remove(spUrl(`${API_AWARD}/${params.id}/reviewed-section/${params.tab}${searchParams}`), options);
			case COLUMN_HEADER_KEYS.PROPOSAL:
				return remove(spUrl(`${API_PROPOSAL}/${params.id}/reviewed-section/${params.tab}${searchParams}`), options);
			case COLUMN_HEADER_KEYS.SUBAWARD:
				return remove(spUrl(`${API_SUBAWARD}/${params.subawardId}/reviewed-section/${params.tab}${searchParams}`), options);
			default:
				return Promise.reject(new Error(`Unexpected type: ${params.type}`));
		}
	},

	/**
	 * @param {{type: string, tab: string, id: string, subawardId?: string }} params
	 * @param {{version: string}} queryParams
	 * @param {{}?} options
	 */
	setReviewedSection: (params, queryParams, options) => {
		let searchParams = buildSearchParams(queryParams);

		switch (params.type) {
			case COLUMN_HEADER_KEYS.AWARD:
				return put(spUrl(`${API_AWARD}/${params.id}/reviewed-section/${params.tab}${searchParams}`), options);
			case COLUMN_HEADER_KEYS.PROPOSAL:
				return put(spUrl(`${API_PROPOSAL}/${params.id}/reviewed-section/${params.tab}${searchParams}`), options);
			case COLUMN_HEADER_KEYS.SUBAWARD:
				return put(spUrl(`${API_SUBAWARD}/${params.subawardId}/reviewed-section/${params.tab}${searchParams}`), options);
			default:
				return Promise.reject(new Error(`Unexpected type: ${params.type}`));
		}
	},

	/**
	 * @param {{id: string}} params
	 * @param {{}?} options
	 */
	getCaseModel: (params, options) =>
		request(`${FLOWABLE_BASE_URL}/cayuse-api/cases/${params.id}/work-form`, options),

	/**
	 * @param {{id: string}} params
	 * @param {{}?} options
	 */
	getDeployment: ({ id }, options) =>
		request(`${FLOWABLE_BASE_URL}/form-api/form-repository/deployments/${id}`, options),

	/**
	 * @param {{}?} options
	 */
	getScripts: (options) => request(`${FLOWABLE_DESIGN_URL}/cayuse-api/scripts`, options),

	/**
	 * @param {{id}} params
	 * @param {{}?} options
	 */
	getBudget: (params, options) => request(`${FLOWABLE_BASE_URL}/cayuse-api/budget-import/${params.id}`, options),

	/**
	 * @param {{url: string}} params
	 * @param body
	 * @param {{}?} options
	 */
	 updateForm: (params, body, options) =>
	 	save(`${params.url}/form`, body, options),

	/**
	 * @param {{url: string}} params
	 * @param {{ version: number }} queryParams
	 * @param body
	 * @param {{}?} options
	 */
	updateFormEntityVersion: (params, queryParams, body, options) =>
		save(`${params.url}/form/save-form-and-get-version${buildSearchParams(queryParams)}`, body, options),
	
	/**
	 * @param {{url: string}} params
	 * @param {{ version: number }} queryParams
	 * @param body
	 * @param {{}?} options
	 */
	saveForm: (params, queryParams, body, options) =>
		put(`${params.url}/form/save-edited-form-and-get-version${buildSearchParams(queryParams)}`, body, options),

	/**
	 * @param {{url: string}} params
	 * @param {{ version: number }} queryParams
	 * @param body
	 * @param {{}?} options
	 */
	editForm: (params, queryParams, body, options) =>
		put(`${params.url}/start-editing${buildSearchParams(queryParams)}`, body, options),
	
	/**
	 * @param {{url: string}} params
	 * @param {{ version: number }} queryParams
	 * @param body
	 * @param {{}?} options
	 */
	cancelEditForm: (params, queryParams, body, options) =>
		put(`${params.url}/end-editing${buildSearchParams(queryParams)}`, body, options),

	/**
	 * @param {{url: string}} params
	 * @param {{ version: number }} queryParams
	 * @param body
	 * @param {{}?} options
	 */
	checkForm: (params, queryParams, body, options) =>
		put(`${params.url}/form/check-edited-form${buildSearchParams(queryParams)}`, body, options),
	
		/**
	 * @param {{url: string}} params
	 * @param {{ version: number }} queryParams
	 * @param body
	 * @param {{}?} options
	 */
	updateLock: (params, queryParams, body, options) =>
		patch(`${params.url}/track-editing${buildSearchParams(queryParams)}`, body, options),

};
